<template>
  <div class="d-flex flex-column">
    <canvas ref="qrCanvas"></canvas>
    <v-btn
      rounded
      x-large
      color="black"
      class="white--text"
      :loading="isPrinting"
      @click="printQRCode"
    >
      {{ $t('print-qr-code') }}
    </v-btn>
  </div>
</template>

<script>
  import QRCode from 'qrcode';
  import { QR_CONFIG } from '@/config/qr-code';
  import { calculateIconDimensions, drawIconOnCanvas } from '@/utils/canvas';
  import { printDataUrl } from '@/utils/print';
  import errorHandler from '@/mixins/errorHandler';

  export default {
    name: 'QRCodeGenerator',

    mixins: [errorHandler],

    props: {
      /**
       * The data to encode in the QR code
       */
      data: {
        type: String,
        required: true,
      },
      /**
       * The path to the icon to overlay on the QR code
       */
      iconSrc: {
        type: String,
        default: () => QR_CONFIG.ICON.DEFAULT_PATH,
      },
      /**
       * The size of the QR code in pixels
       */
      size: {
        type: Number,
        default: () => QR_CONFIG.DEFAULT_SIZE,
      },
    },

    data() {
      return {
        isPrinting: false,
      };
    },

    mounted() {
      this.generateQRCode();
    },

    methods: {
      async generateQRCode() {
        try {
          const canvas = this.$refs.qrCanvas;
          await this.generateBaseQRCode(canvas);
          await this.addIconOverlay(canvas);
          this.$emit('generated');
        } catch (error) {
          this.handleError('Error generating QR code', error);
        }
      },

      async generateBaseQRCode(canvas) {
        const options = {
          errorCorrectionLevel: QR_CONFIG.ERROR_CORRECTION_LEVEL,
          width: this.size,
        };
        await QRCode.toCanvas(canvas, this.data, options);
      },

      addIconOverlay(canvas) {
        return new Promise((resolve, reject) => {
          const icon = new Image();
          icon.onload = () => {
            try {
              const dimensions = calculateIconDimensions(canvas.width, QR_CONFIG.ICON.SIZE_RATIO);
              drawIconOnCanvas(canvas, icon, dimensions);
              resolve();
            } catch (error) {
              reject(error);
            }
          };
          icon.onerror = () => reject(new Error('Failed to load icon'));
          icon.src = this.iconSrc;
        });
      },

      async printQRCode() {
        this.isPrinting = true;
        try {
          const canvas = this.$refs.qrCanvas;
          if (!canvas || !canvas.toDataURL) {
            throw new Error('QR canvas is not available');
          }

          const dataUrl = canvas.toDataURL();
          await printDataUrl(dataUrl);
          this.$emit('printed');
        } catch (error) {
          this.handleError('Error printing QR code', error);
        } finally {
          this.isPrinting = false;
        }
      },
    },
  };
</script>

<style scoped>
  canvas {
    margin: auto;
    display: block;
  }
</style>
